import { useSyncExternalStore } from 'react';

/**
 * What the club portal's quiet translation status line shows.
 *
 * Only the two things a person has to act on: a run that failed, and hand-edited
 * translations whose source text changed and now need a Keep/Re-translate
 * decision. A run that simply did its job says nothing at all — clubs edit
 * content all day, and a running commentary on the machinery behind it ("
 * Translating…", "Translated 1 string into Español") is noise that buries the
 * messages that matter.
 *
 * Neither state expires on a timer: both stay until a later run says otherwise,
 * or until the club follows the link and acts on it.
 */
export type TranslationActivity =
  | { status: 'idle' }
  | {
      status: 'review' | 'error';
      message: string;
      /** Where the club goes to act on it. */
      link?: { href: string; label: string };
    };

let state: TranslationActivity = { status: 'idle' };
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());

export function getTranslationActivity(): TranslationActivity {
  return state;
}

export function subscribeTranslationActivity(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setTranslationActivity(next: TranslationActivity): void {
  state = next;
  emit();
}

/** The notice has been acted on (or there is nothing left to say). */
export function clearTranslationActivity(): void {
  setTranslationActivity({ status: 'idle' });
}

export function useTranslationActivity(): TranslationActivity {
  return useSyncExternalStore(subscribeTranslationActivity, getTranslationActivity, getTranslationActivity);
}
